import { ChevronDown, Search, Undo2 } from 'lucide-react';
import { useId, useState } from 'react';
import { useAuth } from '@/shared/auth';
import { cn } from '@/shared/lib/cn';
import { SupportStartDialog } from './SupportStartDialog';
import { SwitcherWorkspaceList } from './SwitcherWorkspaceList';
import { useEndSupport } from './useEndSupport';
import { useDirectSupportSwitch } from './useDirectSupportSwitch';
import type { SupportTarget } from './useSupportStart';
import { useSwitcherWorkspaces } from './useSwitcherWorkspaces';

const controlClass =
  'text-ink hover:bg-card-alt hover:text-ink focus-visible:outline-forest flex min-h-11 w-full items-center gap-2 rounded-control px-3 text-left text-[13px] font-medium focus-visible:outline focus-visible:outline-2 disabled:opacity-60';

/**
 * „Kunde wechseln“ in der Workspace-Zeile der Seitenleiste, nur für den Plattform-Admin der Heimatsitzung.
 * Ein Kunden-Workspace wechselt direkt, der Code-Dialog erscheint nur bei fehlender Freigabe. Die
 * Liste klappt in der Leiste auf statt als Popover: so verhält sie sich im Handy-Blatt gleich.
 */
export function WorkspaceSwitcher() {
  const { support } = useAuth();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<SupportTarget | null>(null);
  const panelId = useId();
  const list = useSwitcherWorkspaces(open);
  const back = useEndSupport();
  const direct = useDirectSupportSwitch(setTarget);

  return (
    <div className="mt-3">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className={cn(controlClass, 'border-line border text-xs')}
      >
        <span className="flex-1">Kunde wechseln</span>
        <ChevronDown
          size={15}
          aria-hidden
          className={cn(
            'flex-none transition-transform motion-reduce:transition-none',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div id={panelId} className="mt-2 space-y-2">
          {support && (
            <button
              type="button"
              onClick={back.end}
              disabled={back.pending || direct.pending}
              className={cn(controlClass, 'text-xs')}
            >
              <Undo2 size={15} aria-hidden className="flex-none" />
              <span className="min-w-0 flex-1 truncate" title={back.backLabel}>
                {back.backLabel}
              </span>
            </button>
          )}
          <label className="relative block">
            <span className="sr-only">Kunden-Workspace suchen</span>
            <Search
              size={14}
              aria-hidden
              className="text-ink-faint pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            />
            <input
              type="search"
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
              placeholder="Kunden suchen"
              className="border-line bg-card-alt text-ink placeholder:text-ink-faint focus:ring-forest rounded-control min-h-11 w-full border pl-8 pr-2 text-[13px] focus:outline-none focus:ring-2"
            />
          </label>
          <SwitcherWorkspaceList
            list={list}
            currentId={support?.workspaceId ?? null}
            onPick={direct.pick}
            disabled={direct.pending || back.pending}
          />
          {direct.pending && (
            <p role="status" className="text-ink-soft text-xs">
              Workspace wird gewechselt …
            </p>
          )}
        </div>
      )}
      <SupportStartDialog target={target} onClose={() => setTarget(null)} />
    </div>
  );
}
